import type { Contact, PrivateData, RevealBulkResponse } from '../../../domain/contact';
import { revealContact } from '../../../domain/contact';
import type { QuotaRepository } from '../../quota/repository';
import type { UserRepository } from '../../user/repository';

type FactoryDeps = {
  contactRepository: RevealContactRepository;
  quotaRepository: QuotaRepository;
  userRepository: UserRepository;
};

export interface RevealContactRepository {
  getContactById(contactId: string): Contact | undefined;

  saveContact(contact: Contact): void;

  getPrivateData(contact: Contact): Promise<PrivateData | undefined>;

  getBulkPrivateData(contacts: Contact[]): Promise<RevealBulkResponse>;

  getBulkRevealStatus(requestId: string): Promise<number>;

  showLoadingToast(count: number): void;

  showSuccessToast(): void;

  showPartialSuccessToast({ totalCount, successCount }: { totalCount: number; successCount: number }): void;

  showErrorToast(): void;

  addNotRevealedContactId(contactId: string): void;

  invalidateAllContactListQueries(refetch?: boolean): void;
}

const isRevealFailed = (data: PrivateData | undefined) => {
  if (!data) {
    return true;
  }
  const { revealErrors } = data;
  return (
    revealErrors?.hasDirectPhoneNumbersError ||
    revealErrors?.hasMobilePhoneNumbersError ||
    revealErrors?.hasValidEmailsError
  );
};

export const makeRevealContactUseCase =
  ({ contactRepository, quotaRepository, userRepository }: FactoryDeps): RevealContactUseCase =>
  async (contactId: string) => {
    const contact = contactRepository.getContactById(contactId);

    if (!contact) {
      throw new Error('Contact not found');
    }

    const privateData = await contactRepository.getPrivateData(contact);
    if (!privateData) {
      contactRepository.addNotRevealedContactId(contactId);
    }

    contactRepository.saveContact(revealContact(contact, privateData));

    const user = userRepository.getUser();
    if (!user) {
      throw new Error('User not found');
    }

    const isSuccessful = !isRevealFailed(privateData);
    if (isSuccessful) {
      quotaRepository.decreaseContactsQuota();
    }
    return { isSuccessful, privateData };
  };

export const makeRevealContactsBulkUseCase =
  ({ contactRepository, quotaRepository, userRepository }: FactoryDeps): RevealContactsBulkUseCase =>
  async ({ contactsToReveal, withFollowingExport }) => {
    const payload = contactsToReveal.filter((contact) => !contact.isRevealed);
    if (payload.length === 0) {
      throw new Error('Contact not found');
    }

    if (!withFollowingExport) {
      contactRepository.showLoadingToast(payload.length);
    }

    const response = await contactRepository.getBulkPrivateData(payload);
    const { contacts, requestId, notRevealedContactsIds = [], uniqueCount = 0 } = response;

    let revealedContactsCount = uniqueCount;

    if (requestId) {
      const statusResponseCount = await contactRepository.getBulkRevealStatus(requestId);
      revealedContactsCount += statusResponseCount;
      contactRepository.invalidateAllContactListQueries(true);
    }

    notRevealedContactsIds.forEach((contactId) => contactRepository.addNotRevealedContactId(contactId));

    contacts.forEach((privateData) => {
      const contact = payload.find((c) => c.id === privateData.id);
      if (contact) {
        contactRepository.saveContact(revealContact(contact, { ...privateData }));
      }
    });

    const user = userRepository.getUser();
    if (!user) {
      throw new Error('User not found');
    }

    if (revealedContactsCount > 0) {
      quotaRepository.decreaseContactsQuota(revealedContactsCount);

      if (withFollowingExport) {
        return response;
      }

      if (revealedContactsCount < payload.length) {
        contactRepository.showPartialSuccessToast({
          totalCount: contactsToReveal.length,
          successCount: revealedContactsCount,
        });
        return;
      }
      contactRepository.showSuccessToast();
    } else {
      contactRepository.showErrorToast();
    }
  };

export type RevealContactUseCase = (contactId: string) => Promise<{ isSuccessful: boolean; privateData?: PrivateData }>;

export type RevealContactsBulkUseCase = ({
  contactsToReveal,
  withFollowingExport,
}: {
  contactsToReveal: Contact[];
  withFollowingExport?: boolean;
}) => Promise<undefined | RevealBulkResponse>;
